import React from 'react';
import { FormRenderProps } from 'react-final-form';
import { Button, Form, FormError, FormField, FormProps } from '@gik/ui';
import { FormApi } from 'final-form';
import bemBlock from '@gik/core/utils/bemBlock';
import { renderPortal } from '@gik/core/utils/RenderPortal';
import noop from '@gik/core/utils/noop';
import { CalendarClaimFooter } from '../../CalendarClaimFooter';
import { ICalendarEntry } from '@gik/calendar/models/Calendar';
import { IGrubhubClaimDetails } from '@gik/calendar/models/GrubhubClaimDetails';
import { CalendarEventTypeIds, CalendarServiceCategoryIds } from '@gik/calendar/models/CalendarEventTypes';
import { getCalendarClaimFormSchema } from '../schema';
import { IClaimFormValues } from '@gik/calendar';
import { inkindPageOwnerIsRecipient } from '@gik/inkind-page/utils/inkindPagePermissions';
import { useInkindStore } from '@gik/inkind/store/InkindStore';
import { useInkind } from '@gik/api/inkinds/inkind';

interface IGrubhubClaimFormProps extends Omit<FormProps, 'onSubmit'> {
  errorMessage?: React.ReactNode;
  saving?: boolean;
  entry: ICalendarEntry;
  editEventButton?: boolean;
  unclaimButton?: boolean;
  backButton?: boolean;
  buttonsPortal?: () => HTMLElement;
  onSubmit?: (values: IClaimFormValues, form: FormApi) => void | Promise<void>;
  onBack?: () => void | Promise<void>;
  onCancel?: () => void | Promise<void>;
  onUnclaim?: () => void | Promise<void>;
  onEditEvent?: () => void | Promise<void>;
}

export interface IGrubhubClaimFormValues {
  description: string;
  note: string;
}

export default function GrubhubClaimForm({
  className,
  errorMessage,
  entry,
  editEventButton,
  unclaimButton,
  saving,
  backButton,
  buttonsPortal,
  onBack = noop,
  onCancel = noop,
  onUnclaim = noop,
  onEditEvent = noop,
  ...otherProps
}: IGrubhubClaimFormProps): React.ReactElement {
  const bem = bemBlock('provide-myself-claim-form');
  const formId = 'GrubhubClaimForm';

  const inkindRouteId = useInkindStore(state => state.inkindRouteId);
  const { data: inkind } = useInkind(inkindRouteId);

  const claim = entry.claim as IGrubhubClaimDetails;
  const isEditing = !!claim;

  return (
    <Form
      key={JSON.stringify(saving)}
      schema={getCalendarClaimFormSchema(entry, CalendarServiceCategoryIds.LocalRestaurant)}
      initialValues={{
        description: entry.claim?.description,
        note: entry.claim?.note,
      }}
      vertical
      className={bem(null, null, className)}
      id={formId}
      render={(form: FormRenderProps) => {
        let _buttons;

        const fieldsDisabled = form.submitting;
        const isSubmitting = form.submitting || saving;

        if (isEditing) {
          _buttons = (
            <CalendarClaimFooter
              form={form}
              editEventButton={editEventButton}
              unclaimButton={unclaimButton}
              onEditEvent={onEditEvent}
              onUnclaim={onUnclaim}
              backButton={backButton}
              onBack={onBack}
            >
              {/* <Button
                prepend={<SvgIcon name="TrashOutline" />}
                key="unclaim"
                variant="danger"
                onClick={onUnclaim}
                loading={isSubmitting}
                wide
              >
                Unclaim
              </Button> */}
              <Button wide type="submit" key="save" form={formId} disabled={!form.valid} loading={isSubmitting}>
                Save
              </Button>
            </CalendarClaimFooter>
          );
        } else {
          _buttons = (
            <CalendarClaimFooter onCancel={onCancel} backButton onBack={onBack}>
              <Button type="submit" wide form={formId} disabled={!form.valid} loading={isSubmitting}>
                CONFIRM
              </Button>
            </CalendarClaimFooter>
          );
        }

        const showDescription =
          entry.typeId !== CalendarEventTypeIds.Groceries && entry.typeId !== CalendarEventTypeIds.Other;

        const showNotetoRecipient = inkindPageOwnerIsRecipient(inkind);

        return (
          <>
            {entry.typeId === CalendarEventTypeIds.Transportation && (
              <FormField disabled={fieldsDisabled} name="carType" />
            )}
            {showDescription && <FormField disabled={fieldsDisabled} name="description" />}
            {showNotetoRecipient && <FormField disabled={fieldsDisabled} name="note" />}

            {errorMessage && <FormError message={errorMessage} centered />}

            {renderPortal(_buttons, buttonsPortal)}
          </>
        );
      }}
      {...otherProps}
    />
  );
}
